// A question's custom validations: the admin chooses each rule from a dropdown that offers the rules
// the input type takes, and it shows in its own box, in the order chosen, until removed. A rule that
// isn't chosen keeps its fields disabled, so the browser leaves them out of the request (Event Settings
// posts every question at once).
import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["inputType", "row", "integerOnly", "section", "picker", "list", "empty"]

  // Listened to here, not through a data-action: the settings pages' nested-form script (Kegs.Form)
  // swallows clicks on any element that has one, and its data-action="remove" deletes the question.
  connect() {
    if (!this.hasInputTypeTarget) return

    this.choose = this.choose.bind(this)
    this.remove = this.remove.bind(this)
    this.showDefaultMessage = this.showDefaultMessage.bind(this)
    this.pickerTarget.addEventListener("change", this.choose)
    this.element.addEventListener("click", this.remove)
    // In this order, so the default message reads the decimal with its point.
    this.element.addEventListener("input", this.useDecimalPoint)
    this.element.addEventListener("input", this.showDefaultMessage)
    this.filterByInputType()
  }

  disconnect() {
    if (!this.hasInputTypeTarget) return

    this.pickerTarget.removeEventListener("change", this.choose)
    this.element.removeEventListener("click", this.remove)
    this.element.removeEventListener("input", this.useDecimalPoint)
    this.element.removeEventListener("input", this.showDefaultMessage)
  }

  // Shows the chosen rules the input type takes and offers the others it takes; hides the whole
  // section when it takes none.
  filterByInputType() {
    const inputType = this.inputTypeTarget.value
    const applicableRows = this.rowTargets.filter((row) => row.dataset.inputTypes.split(" ").includes(inputType))
    const shownRows = applicableRows.filter((row) => this.isChosen(row))

    this.rowTargets.forEach((row) => row.classList.toggle("d-none", !shownRows.includes(row)))
    this.emptyTarget.classList.toggle("d-none", shownRows.length > 0)
    this.sectionTarget.classList.toggle("d-none", applicableRows.length === 0)
    this.offer(applicableRows.filter((row) => !this.isChosen(row)))

    if (this.hasIntegerOnlyTarget) {
      this.integerOnlyTarget.classList.toggle("d-none", inputType !== "number")
    }
  }

  // A newly chosen rule goes last. A saved rule chosen again after Remove keeps its place: it was
  // never deleted, so it keeps its position after the save too.
  choose() {
    const row = this.rowTargets.find((candidate) => candidate.dataset.key === this.pickerTarget.value)
    if (!row) return

    this.destroyField(row).disabled = false
    this.destroyField(row).value = "0"
    this.toggleFields(row, true)

    if (!("persisted" in row.dataset)) {
      this.listTarget.append(row)
    }

    this.filterByInputType()
  }

  // A saved rule posts _destroy=1 so the save deletes it; a new one posts nothing.
  remove(event) {
    const button = event.target.closest("[data-custom-validation-remove]")
    if (!button) return

    const row = button.closest("[data-kegs--custom-validations-target~='row']")

    if ("persisted" in row.dataset) {
      this.destroyField(row).value = "1"
    } else {
      this.destroyField(row).disabled = true
    }

    this.toggleFields(row, false)
    this.filterByInputType()
    this.pickerTarget.focus()
  }

  // Rewrites the message's placeholder, the rule's default message, with the setting as the admin types
  // it, from the wordings QuestionCustomValidation#default_message_templates renders on the setting.
  showDefaultMessage(event) {
    const templates = event.target.dataset.defaultMessageTemplates
    if (!templates) return

    const { one, other, missing } = JSON.parse(templates)
    const setting = this.savedSetting(event.target.value)
    const row = event.target.closest("[data-kegs--custom-validations-target~='row']")
    const wording = setting === "" ? missing : (setting === "1" && one) || other

    row.querySelector("[data-custom-validation-message]").placeholder =
      wording.replace(event.target.dataset.settingMarker, setting)
  }

  // A decimal setting shows the point it's saved with, as QuestionCustomValidation.setting stores it: a
  // typed comma becomes one, the caret staying where it was.
  useDecimalPoint(event) {
    const field = event.target
    if (field.inputMode !== "decimal" || !field.value.includes(",")) return

    const { selectionStart, selectionEnd } = field
    field.value = field.value.replaceAll(",", ".")
    field.setSelectionRange(selectionStart, selectionEnd)
  }

  // The setting as the saved default message shows it: a number as the server prints it (0.250 shows
  // 0.25, 01 shows 1); none for anything else, which the save rejects.
  savedSetting(value) {
    const setting = value.trim()
    const number = Number(setting)

    return setting === "" || !Number.isFinite(number) ? "" : String(number)
  }

  isChosen(row) {
    const destroyField = this.destroyField(row)

    return !destroyField.disabled && destroyField.value === "0"
  }

  // Rebuilds the dropdown with the given rules, in the order of QuestionCustomValidation::TYPES.
  offer(rows) {
    const prompt = this.pickerTarget.options[0]
    const options = rows
      .sort((first, second) => first.dataset.position - second.dataset.position)
      .map((row) => new Option(row.dataset.name, row.dataset.key))

    this.pickerTarget.replaceChildren(prompt, ...options)
    this.pickerTarget.value = ""
  }

  destroyField(row) {
    return row.querySelector("[data-custom-validation-destroy]")
  }

  // MDL marks each field's own wrapper is-disabled when it upgrades it (on page load) and refreshes it
  // only on that field's events, so a chosen rule would look disabled until typed in. A wrapper not
  // upgraded yet, when a rule is chosen before the page finishes loading, takes the state on upgrade.
  toggleFields(row, enabled) {
    row.querySelectorAll("[data-custom-validation-field]").forEach((field) => {
      field.disabled = !enabled
      field.closest(".mdl-textfield").MaterialTextfield?.checkDisabled()
    })
  }
}
